import React, { useState } from 'react';
import { Apple, Plus, Trash2 } from 'lucide-react';
import {
  Alert, Badge, Button, Card, Empty, Field, Input, Page, PageHead, Select, Stat,
} from '../components/ui';
import { MACROS, MEAL_TIMES, n0, timeOf, titleise } from '../lib/format';

const BLANK = { name: '', calories: '', protein: '', carbs: '', fats: '', meal_time: 'breakfast' };

export default function FoodLog({ foodLog, onAdd, onDelete }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setError('');
    try {
      await onAdd({
        name: form.name.trim(),
        meal_time: form.meal_time,
        calories: parseFloat(form.calories) || 0,
        protein: parseFloat(form.protein) || 0,
        carbs: parseFloat(form.carbs) || 0,
        fats: parseFloat(form.fats) || 0,
      });
      setForm(BLANK);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const totals = foodLog.reduce(
    (a, f) => ({
      calories: a.calories + (+f.calories || 0),
      protein: a.protein + (+f.protein || 0),
      carbs: a.carbs + (+f.carbs || 0),
      fats: a.fats + (+f.fats || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fats: 0 }
  );

  const byMeal = MEAL_TIMES.map((m) => ({
    meal: m,
    items: foodLog.filter((f) => f.meal_time === m),
  })).filter((g) => g.items.length);

  return (
    <Page>
      <PageHead title="Food Log">Everything you have eaten today.</PageHead>

      <div className="grid grid-4">
        {MACROS.map((m) => (
          <Stat key={m.key} label={m.label} value={n0(totals[m.key])} unit={m.unit} />
        ))}
      </div>

      <Card title="Add a meal">
        <form onSubmit={submit}>
          {error && <div style={{ marginBottom: 12 }}><Alert tone="crit">{error}</Alert></div>}
          <div className="grid" style={{ gridTemplateColumns: 'minmax(200px, 2.2fr) minmax(130px, 1fr)' }}>
            <Field label="Food">
              <Input placeholder="Grilled chicken breast" value={form.name} onChange={set('name')} required />
            </Field>
            <Field label="Meal">
              <Select
                options={MEAL_TIMES.map((m) => ({ value: m, label: titleise(m) }))}
                value={form.meal_time}
                onChange={set('meal_time')}
              />
            </Field>
          </div>
          <div className="grid grid-4" style={{ marginTop: 14 }}>
            {MACROS.map((m) => (
              <Field key={m.key} label={`${m.label} · ${m.unit}`}>
                <Input numeric type="number" step="any" min="0" placeholder="0"
                  value={form[m.key]} onChange={set(m.key)} />
              </Field>
            ))}
          </div>
          <Button variant="primary" icon={Plus} type="submit" disabled={busy || !form.name.trim()}
            loading={busy} style={{ marginTop: 16 }}>
            Add to log
          </Button>
        </form>
      </Card>

      <Card title={`Today (${foodLog.length})`} flush>
        {foodLog.length === 0 ? (
          <Empty icon={Apple} title="Nothing logged yet">
            Add what you have eaten and your macro totals update above.
          </Empty>
        ) : (
          byMeal.map((group) => (
            <div key={group.meal}>
              <div className="label" style={{ padding: '12px 16px 6px' }}>
                {titleise(group.meal)} · {n0(group.items.reduce((s, f) => s + (+f.calories || 0), 0))} kcal
              </div>
              {group.items.map((f) => (
                <div className="item" key={f.id}>
                  <div className="grow">
                    <div className="item-title">{f.name}</div>
                    <div className="item-sub">{timeOf(f.logged_at)}</div>
                  </div>
                  <div className="row-tight" style={{ gap: 14 }}>
                    {MACROS.slice(1).map((m) => (
                      <span key={m.key} className="num" style={{ fontSize: 12, color: m.color }}>
                        {n0(f[m.key])}<span className="faint">{m.unit}</span>
                      </span>
                    ))}
                    <Badge tone="muted">{n0(f.calories)} kcal</Badge>
                  </div>
                  <Button size="sm" variant="danger" icon={Trash2}
                    onClick={() => onDelete(f.id)} aria-label={`Delete ${f.name}`} />
                </div>
              ))}
            </div>
          ))
        )}
      </Card>
    </Page>
  );
}
